import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Steps,
  Select,
  Input,
  Button,
  Tag,
  Empty,
  Spin,
  Alert,
  Tooltip,
  Switch,
  Avatar,
  message,
} from "antd";
import {
  CheckCircleFilled,
  PlusOutlined,
  ArrowRightOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PictureOutlined,
  SaveOutlined,
  FileDoneOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { DidacticRoutesAPI, AssistanceApi } from "../../../../api";
import { TeacherTasksAPI } from "../../../../api/teacherTasksAPI";
import { QuizApi } from "../../../../api/quiz";
import { StudentAPI } from "../../../../api/studentAPI";
import { getShareScopeLabel } from "../../../../Utils/shareScope";
import styles from "./TaskCreateWizard.module.scss";

// Ilustracion generica de itinerario (R3: no hay banner por ruta).
const ROUTE_ILLUSTRATION =
  "https://storage.googleapis.com/educa360-legacy-assets/legacy/repopublic-educa360-central/adminweb/virtualscenes/PubliEduca360/Itinerario/1754473963925_itinerarioB.jpg";

/**
 * TaskCreateWizard — asistente de creacion de tareas (F3.3, mockups paso1-4).
 *
 * Autonomo (no depende del grid legacy). Entrega Paso 1 (Clase + Titulo +
 * itinerario) y Paso 2 (Biblioteca de escenas + Flujo de pasos, con Guardar
 * borrador). Pasos 3-4 = placeholder (siguiente iteracion).
 *
 * Props:
 *  - teacher (objeto), accessToken
 *  - onCreated()  -> recargar lista al guardar
 *  - onClose()    -> cerrar el Drawer
 */
export function TaskCreateWizard({
  teacher,
  accessToken,
  onCreated,
  onClose,
  editTaskId = null,
}) {
  const { t } = useTranslation("tasks");
  const isEdit = !!editTaskId;
  const routesApi = useMemo(() => new DidacticRoutesAPI(), []);
  const tasksApi = useMemo(() => new TeacherTasksAPI(), []);
  const quizApi = useMemo(() => new QuizApi(), []);
  const studentApi = useMemo(() => new StudentAPI(), []);
  const assistanceApi = useMemo(() => new AssistanceApi(), []);

  const teacherId = teacher?._id || teacher?.id || null;
  const institutionId =
    teacher?.institution?._id || teacher?.institution || null;

  const [current, setCurrent] = useState(0);

  // ---- Paso 1 ----
  const initialClass = useMemo(
    () =>
      (Array.isArray(teacher?.classrole) &&
        teacher.classrole[0]?.classnumber) ||
      undefined,
    [teacher],
  );
  const [classnumber, setClassnumber] = useState(initialClass);
  const [title, setTitle] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeError, setRouteError] = useState("");

  // ---- Paso 2 ----
  const [scenes, setScenes] = useState([]);
  const [loadingScenes, setLoadingScenes] = useState(false);
  const [sceneError, setSceneError] = useState("");
  const [search, setSearch] = useState("");
  const [steps, setSteps] = useState([]);

  // ---- Guardar / edicion ----
  const [creating, setCreating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // ---- Paso 4: estudiantes de la clase ----
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // ---- Paso 3: cuestionario final ----
  const [finalQuizRequired, setFinalQuizRequired] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizError, setQuizError] = useState("");

  // ---- Paso 3: asistente IA (opcional) ----
  const [assistantsList, setAssistantsList] = useState([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState(null);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  const classOptions = useMemo(() => {
    const roles = Array.isArray(teacher?.classrole) ? teacher.classrole : [];
    const uniques = [
      ...new Set(roles.map((r) => r.classnumber).filter(Boolean)),
    ];
    return uniques.map((cn) => ({ value: cn, label: cn }));
  }, [teacher]);

  const wizardSteps = [
    { title: t("wizard.stepClass"), sub: t("wizard.stepClassSub") },
    { title: t("wizard.stepScenes"), sub: t("wizard.stepScenesSub") },
    { title: t("wizard.stepQuiz"), sub: t("wizard.stepQuizSub") },
    { title: t("wizard.stepReview"), sub: t("wizard.stepReviewSub") },
  ];

  // ---- Carga de itinerarios ----
  useEffect(() => {
    let ignore = false;
    if (!teacherId) return undefined;
    (async () => {
      try {
        setLoadingRoutes(true);
        setRouteError("");
        const res = await routesApi.getRoutesByTeacher(accessToken, teacherId);
        if (!ignore) setRoutes(res?.routes || []);
      } catch (e) {
        if (!ignore) setRouteError(e?.message || t("wizard.routesError"));
      } finally {
        if (!ignore) setLoadingRoutes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [teacherId, accessToken, routesApi, t]);

  // ---- Carga de escenas de la ruta seleccionada ----
  useEffect(() => {
    let ignore = false;
    if (!selectedRouteId) {
      setScenes([]);
      return undefined;
    }
    (async () => {
      try {
        setLoadingScenes(true);
        setSceneError("");
        const res = await routesApi.getScenesByRoute(
          accessToken,
          selectedRouteId,
          1,
          50,
        );
        if (!ignore) setScenes(res?.scenes || []);
      } catch (e) {
        if (!ignore) setSceneError(e?.message || t("wizard.scenesError"));
      } finally {
        if (!ignore) setLoadingScenes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [selectedRouteId, accessToken, routesApi, t]);

  // ---- Precarga en modo edicion (solo borradores) ----
  useEffect(() => {
    let ignore = false;
    if (!editTaskId) return undefined;
    (async () => {
      try {
        setLoadingEdit(true);
        const { task } = await tasksApi.getTaskById(accessToken, editTaskId);
        if (ignore || !task) return;
        setClassnumber(task.classnumber || undefined);
        setTitle(task.title || "");
        const loaded = (task.steps || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((s, idx) => ({
            scene: s.scene,
            sceneTitle: s.sceneTitle || t("wizard.sceneNoTitle"),
            sceneBanner: s.sceneBanner || null,
            instruction: s.instruction || "",
            order: idx + 1,
          }));
        setSteps(loaded);
        if (task.finalQuizRequired) {
          setFinalQuizRequired(true);
          setSelectedQuizId(task.finalQuiz || null);
        }
        if (task.assistantActive_Id) {
          setSelectedAssistantId(
            task.assistantActive_Id._id || task.assistantActive_Id,
          );
        }
        // En edicion arrancamos en el Paso 2 (escenas), que es lo habitual a tocar
        setCurrent(1);
      } catch (e) {
        message.error(e?.message || t("detail.loadError"));
      } finally {
        if (!ignore) setLoadingEdit(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [editTaskId, accessToken, tasksApi, t]);

  // ---- Carga de cuestionarios cuando se activa el toggle ----
  useEffect(() => {
    let ignore = false;
    if (!finalQuizRequired || !teacherId) {
      return undefined;
    }
    (async () => {
      try {
        setLoadingQuizzes(true);
        setQuizError("");
        const res = await quizApi.getAllQuizzes(accessToken, teacherId);
        const list = Array.isArray(res?.quizzes)
          ? res.quizzes
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
        if (!ignore) setQuizzes(list);
      } catch (e) {
        if (!ignore) setQuizError(e?.message || t("wizard.quizzesError"));
      } finally {
        if (!ignore) setLoadingQuizzes(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [finalQuizRequired, teacherId, accessToken, quizApi, t]);

  // ---- Carga de asistentes IA disponibles (para el selector opcional) ----
  useEffect(() => {
    let ignore = false;
    if (!accessToken) return undefined;
    (async () => {
      try {
        setLoadingAssistants(true);
        const res = await assistanceApi.getAssistances(accessToken, 1, 100);
        const list = Array.isArray(res?.assistances?.docs)
          ? res.assistances.docs
          : Array.isArray(res?.assistances)
            ? res.assistances
            : [];
        if (!ignore) setAssistantsList(list);
      } catch (e) {
        if (!ignore) setAssistantsList([]);
      } finally {
        if (!ignore) setLoadingAssistants(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [accessToken, assistanceApi]);

  const assistantOptions = useMemo(
    () =>
      (assistantsList || [])
        .map((a) => {
          const id = a?._id || a?.id;
          if (!id) return null;
          return { value: id, label: a?.title || String(id).slice(-6) };
        })
        .filter(Boolean),
    [assistantsList],
  );

  const quizOptions = useMemo(
    () =>
      (quizzes || [])
        .map((q) => {
          const id = q?._id || q?.id;
          if (!id) return null;
          return {
            value: id,
            label: q?.title || q?.name || String(id).slice(-6),
          };
        })
        .filter(Boolean),
    [quizzes],
  );

  const canNextFrom1 =
    !!classnumber && !!title.trim() && (!!selectedRouteId || steps.length > 0);
  const canSave = steps.length > 0 && !!classnumber && !!title.trim();

  const handleSelectRoute = useCallback(
    (route) => {
      setSelectedRouteId(route._id);
      if (route.classnumber && !classnumber) setClassnumber(route.classnumber);
    },
    [classnumber],
  );

  // ---- Helpers de pasos ----
  const addScene = useCallback(
    (scene) => {
      const sceneId = scene?._id || scene?.id;
      if (!sceneId) return;
      setSteps((arr) => {
        if (arr.some((s) => String(s.scene) === String(sceneId))) return arr;
        return [
          ...arr,
          {
            scene: sceneId,
            sceneTitle: scene?.title || t("wizard.sceneNoTitle"),
            sceneBanner: scene?.bannertopUrl || scene?.bannerTopUrl || null,
            instruction: "",
            order: arr.length + 1,
          },
        ];
      });
    },
    [t],
  );

  const removeStep = useCallback((i) => {
    setSteps((arr) =>
      arr
        .filter((_, idx) => idx !== i)
        .map((s, idx) => ({ ...s, order: idx + 1 })),
    );
  }, []);

  const moveStep = useCallback((i, dir) => {
    setSteps((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((s, idx) => ({ ...s, order: idx + 1 }));
    });
  }, []);

  const changeInstruction = useCallback((i, value) => {
    setSteps((arr) => {
      const next = [...arr];
      next[i] = { ...next[i], instruction: value || "" };
      return next;
    });
  }, []);

  const addedIds = useMemo(
    () => new Set(steps.map((s) => String(s.scene))),
    [steps],
  );

  const filteredScenes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scenes;
    return scenes.filter((s) => (s.title || "").toLowerCase().includes(q));
  }, [scenes, search]);

  // ---- Guardar borrador ----
  const saveDraft = useCallback(async () => {
    if (!canSave) return;
    try {
      setCreating(true);
      const payload = tasksApi.buildTaskPayload({
        title: title.trim(),
        classnumber,
        steps,
        finalQuizRequired,
        finalQuiz: selectedQuizId,
        schedule: { startAt: null, endAt: null },
      });
      payload.assistantActive_Id = selectedAssistantId || "";
      if (isEdit) {
        await tasksApi.updateTask(accessToken, editTaskId, payload);
        message.success(t("wizard.changesSaved"));
      } else {
        await tasksApi.createTask(accessToken, teacherId, classnumber, payload);
        message.success(t("wizard.draftSaved"));
      }
      await onCreated?.();
      onClose?.();
    } catch (e) {
      message.error(e?.message || t("grid.draftCreateError"));
    } finally {
      setCreating(false);
    }
  }, [
    canSave,
    tasksApi,
    title,
    classnumber,
    steps,
    finalQuizRequired,
    selectedQuizId,
    selectedAssistantId,
    accessToken,
    teacherId,
    isEdit,
    editTaskId,
    t,
    onCreated,
    onClose,
  ]);

  // ---- Cargar estudiantes al llegar al Paso 4 ----
  useEffect(() => {
    let ignore = false;
    if (current !== 3 || !classnumber || !institutionId) return undefined;
    (async () => {
      try {
        setLoadingStudents(true);
        const resp = await studentApi.getStudentsByClassnumber(
          accessToken,
          institutionId,
          classnumber,
          1,
          50,
        );
        if (!ignore)
          setStudents(Array.isArray(resp?.students) ? resp.students : []);
      } catch (e) {
        if (!ignore) setStudents([]);
      } finally {
        if (!ignore) setLoadingStudents(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [current, classnumber, institutionId, accessToken, studentApi]);

  const quizLabel = useMemo(() => {
    if (!finalQuizRequired || !selectedQuizId) return null;
    const opt = quizOptions.find((o) => o.value === selectedQuizId);
    return opt?.label || null;
  }, [finalQuizRequired, selectedQuizId, quizOptions]);

  const assistantLabel = useMemo(() => {
    if (!selectedAssistantId) return null;
    const opt = assistantOptions.find((o) => o.value === selectedAssistantId);
    return opt?.label || null;
  }, [selectedAssistantId, assistantOptions]);

  // ---- Activar tarea (create/update + activate encadenados) ----
  const activateTask = useCallback(async () => {
    if (!canSave) return;
    try {
      setActivating(true);
      const payload = tasksApi.buildTaskPayload({
        title: title.trim(),
        classnumber,
        steps,
        finalQuizRequired,
        finalQuiz: selectedQuizId,
        schedule: { startAt: null, endAt: null },
      });
      payload.assistantActive_Id = selectedAssistantId || "";
      let taskId = editTaskId;
      if (isEdit) {
        await tasksApi.updateTask(accessToken, editTaskId, payload);
      } else {
        const res = await tasksApi.createTask(
          accessToken,
          teacherId,
          classnumber,
          payload,
        );
        taskId = res?.task?._id || res?.task?.id || null;
      }
      if (taskId) await tasksApi.activateTask(accessToken, taskId);
      message.success(t("wizard.activated"));
      await onCreated?.();
      onClose?.();
    } catch (e) {
      message.error(e?.message || t("wizard.activateError"));
    } finally {
      setActivating(false);
    }
  }, [
    canSave,
    tasksApi,
    title,
    classnumber,
    steps,
    finalQuizRequired,
    selectedQuizId,
    selectedAssistantId,
    accessToken,
    teacherId,
    isEdit,
    editTaskId,
    t,
    onCreated,
    onClose,
  ]);

  const goNext = () =>
    setCurrent((c) => Math.min(c + 1, wizardSteps.length - 1));
  const goBack = () => setCurrent((c) => Math.max(c - 1, 0));

  // ============== Paso 1 ==============
  const renderStep1 = () => (
    <div className={styles.stepBody}>
      <div className={styles.metaRow}>
        <label className={styles.field}>
          <span className={styles.label}>{t("wizard.classLabel")}</span>
          <Select
            size="large"
            placeholder={t("classSelector.placeholder")}
            options={classOptions}
            value={classnumber}
            onChange={setClassnumber}
            className={styles.fullw}
            showSearch
            optionFilterProp="label"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>
            <span className={styles.req}>*</span> {t("wizard.titleLabel")}
          </span>
          <Input
            size="large"
            placeholder={t("grid.taskTitlePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
      </div>

      <div className={styles.sectionHead}>
        <h4 className={styles.sectionTitle}>{t("wizard.chooseItinerary")}</h4>
        <p className={styles.sectionSub}>{t("wizard.chooseItinerarySub")}</p>
      </div>

      {loadingRoutes ? (
        <div className={styles.center}>
          <Spin />
        </div>
      ) : routeError ? (
        <Alert type="error" showIcon message={routeError} />
      ) : routes.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("wizard.noItineraries")}
        />
      ) : (
        <div className={styles.routeGrid}>
          {routes.map((route) => {
            const selected = route._id === selectedRouteId;
            return (
              <button
                type="button"
                key={route._id}
                className={`${styles.routeCard} ${selected ? styles.routeCardSel : ""}`}
                onClick={() => handleSelectRoute(route)}
              >
                {selected ? (
                  <CheckCircleFilled className={styles.routeCheck} />
                ) : null}
                <div className={styles.routeIllu}>
                  <img src={ROUTE_ILLUSTRATION} alt="" />
                </div>
                <div className={styles.routeName} title={route.nameRute}>
                  {route.nameRute}
                </div>
                <Tag className={styles.routeBadge}>
                  {getShareScopeLabel(route)}
                </Tag>
                <span
                  className={`${styles.routeBtn} ${selected ? styles.routeBtnSel : ""}`}
                >
                  {selected ? t("wizard.selectedBtn") : t("wizard.selectBtn")}
                </span>
              </button>
            );
          })}

          <button
            type="button"
            className={`${styles.routeCard} ${styles.routeCreate}`}
            onClick={() => message.info(t("wizard.createItineraryHint"))}
          >
            <div className={`${styles.routeIllu} ${styles.routeIlluGhost}`}>
              <PlusOutlined />
            </div>
            <div className={styles.routeName}>{t("wizard.newItinerary")}</div>
            <span className={`${styles.routeBtn} ${styles.routeBtnGhost}`}>
              {t("wizard.createBtn")} <PlusOutlined />
            </span>
          </button>
        </div>
      )}
    </div>
  );

  // ============== Paso 2 ==============
  const renderStep2 = () => (
    <div className={styles.twoCol}>
      {/* Biblioteca */}
      <section className={styles.libCol}>
        <h4 className={styles.colTitle}>{t("wizard.libraryTitle")}</h4>
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined />}
          placeholder={t("wizard.searchScenes")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.search}
        />

        {loadingScenes ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : sceneError ? (
          <Alert type="error" showIcon message={sceneError} />
        ) : filteredScenes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("wizard.noScenes")}
          />
        ) : (
          <div className={styles.sceneGrid}>
            {filteredScenes.map((scene) => {
              const added = addedIds.has(String(scene._id));
              return (
                <div key={scene._id} className={styles.sceneCard}>
                  <div className={styles.sceneThumb}>
                    {scene.bannertopUrl ? (
                      <img src={scene.bannertopUrl} alt={scene.title} />
                    ) : (
                      <PictureOutlined className={styles.sceneThumbIcon} />
                    )}
                  </div>
                  <div className={styles.sceneBody}>
                    <div className={styles.sceneName} title={scene.title}>
                      {scene.title}
                    </div>
                    {Array.isArray(scene.category) && scene.category.length ? (
                      <div className={styles.sceneCat}>
                        {scene.category.join(", ")}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    size="small"
                    type={added ? "default" : "primary"}
                    icon={added ? <CheckCircleFilled /> : <PlusOutlined />}
                    disabled={added}
                    onClick={() => addScene(scene)}
                    className={styles.addBtn}
                  >
                    {added ? t("wizard.added") : t("wizard.addScene")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Flujo */}
      <section className={styles.flowCol}>
        <h4 className={styles.colTitle}>{t("wizard.flowTitle")}</h4>

        {steps.length === 0 ? (
          <div className={styles.flowEmpty}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("wizard.flowEmpty")}
            />
          </div>
        ) : (
          <ol className={styles.flowList}>
            {steps.map((step, i) => (
              <li className={styles.flowItem} key={step.scene}>
                <span className={styles.flowNum}>{i + 1}</span>
                <div className={styles.flowThumb}>
                  {step.sceneBanner ? (
                    <img src={step.sceneBanner} alt="" />
                  ) : (
                    <PictureOutlined className={styles.sceneThumbIcon} />
                  )}
                </div>
                <div className={styles.flowBody}>
                  <div className={styles.flowName}>{step.sceneTitle}</div>
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    placeholder={t("editor.stepInstructionPlaceholder")}
                    value={step.instruction}
                    onChange={(e) => changeInstruction(i, e.target.value)}
                    className={styles.flowInstruction}
                  />
                </div>
                <div className={styles.flowActions}>
                  <Tooltip title={t("editor.moveUp")}>
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={i === 0}
                      onClick={() => moveStep(i, -1)}
                    />
                  </Tooltip>
                  <Tooltip title={t("editor.moveDown")}>
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={i === steps.length - 1}
                      onClick={() => moveStep(i, 1)}
                    />
                  </Tooltip>
                  <Tooltip title={t("editor.deleteStep")}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeStep(i)}
                    />
                  </Tooltip>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );

  // ============== Paso 3: cuestionario final ==============
  const renderStep3 = () => (
    <div className={styles.quizStep}>
      <div className={styles.quizCard}>
        <div className={styles.quizControls}>
          <div className={styles.quizToggleRow}>
            <Switch
              checked={finalQuizRequired}
              onChange={(checked) => {
                setFinalQuizRequired(checked);
                if (!checked) {
                  setSelectedQuizId(null);
                }
              }}
            />
            <span className={styles.quizToggleLabel}>
              {t("wizard.requireQuiz")}
            </span>
          </div>

          {finalQuizRequired ? (
            <div className={styles.quizSelectWrap}>
              <span className={styles.label}>{t("wizard.selectQuiz")}</span>
              <Select
                size="large"
                className={styles.fullw}
                placeholder={t("wizard.quizPlaceholder")}
                options={quizOptions}
                value={selectedQuizId || undefined}
                onChange={(v) => setSelectedQuizId(v || null)}
                loading={loadingQuizzes}
                allowClear
                showSearch
                optionFilterProp="label"
                notFoundContent={
                  loadingQuizzes ? <Spin size="small" /> : t("wizard.noQuizzes")
                }
              />
              {quizError ? (
                <Alert
                  type="warning"
                  showIcon
                  message={quizError}
                  className={styles.quizAlert}
                />
              ) : null}
              <p className={styles.quizHelper}>{t("wizard.quizHelper")}</p>
            </div>
          ) : (
            <p className={styles.quizHelper}>{t("wizard.quizOptional")}</p>
          )}
        </div>

        <div className={styles.quizIllu}>
          <FileDoneOutlined />
        </div>
      </div>

      {/* Asistente IA (opcional): el alumno lo vera/chateara durante la tarea */}
      <div className={styles.quizCard}>
        <div className={styles.quizControls}>
          <span className={styles.label}>{t("wizard.assistantLabel")}</span>
          <Select
            size="large"
            className={styles.fullw}
            placeholder={t("wizard.assistantPlaceholder")}
            options={assistantOptions}
            value={selectedAssistantId || undefined}
            onChange={(v) => setSelectedAssistantId(v || null)}
            loading={loadingAssistants}
            allowClear
            showSearch
            optionFilterProp="label"
            notFoundContent={
              loadingAssistants ? (
                <Spin size="small" />
              ) : (
                t("wizard.noAssistants")
              )
            }
          />
          <p className={styles.quizHelper}>{t("wizard.assistantHelper")}</p>
        </div>
        <div className={styles.quizIllu}>
          <RobotOutlined />
        </div>
      </div>
    </div>
  );

  // ============== Paso 4: revisar y activar ==============
  const studentName = (s) =>
    s.name ||
    [s.firstname, s.surname || s.lastname].filter(Boolean).join(" ") ||
    s.email ||
    "-";

  const renderStep4 = () => (
    <div className={styles.twoCol}>
      {/* Resumen */}
      <section className={styles.summaryCol}>
        <h4 className={styles.colTitle}>{t("wizard.summaryTitle")}</h4>

        <dl className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <dt>{t("wizard.sumClass")}</dt>
            <dd>{classnumber || "-"}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t("wizard.sumTitle")}</dt>
            <dd>{title || "-"}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t("wizard.sumSteps")}</dt>
            <dd>{t("detail.stepsTitle", { count: steps.length })}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t("wizard.sumQuiz")}</dt>
            <dd>
              {finalQuizRequired
                ? quizLabel
                  ? `${t("wizard.quizYesShort")} - ${quizLabel}`
                  : t("wizard.quizYesShort")
                : t("detail.quizNone")}
            </dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t("wizard.assistantLabel")}</dt>
            <dd>{assistantLabel || t("wizard.assistantNone")}</dd>
          </div>
        </dl>

        {steps.length ? (
          <div className={styles.summaryThumbs}>
            {steps.map((s) => (
              <div className={styles.summaryThumb} key={s.scene}>
                {s.sceneBanner ? (
                  <img src={s.sceneBanner} alt={s.sceneTitle} />
                ) : (
                  <PictureOutlined className={styles.sceneThumbIcon} />
                )}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {/* Estudiantes */}
      <section className={styles.studentsCol}>
        <div className={styles.studentsHead}>
          <h4 className={styles.colTitle}>{t("wizard.studentsTitle")}</h4>
          {classnumber ? (
            <Tag className={styles.classBadge}>{classnumber}</Tag>
          ) : null}
        </div>

        {loadingStudents ? (
          <div className={styles.center}>
            <Spin />
          </div>
        ) : students.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("wizard.noStudents")}
          />
        ) : (
          <ul className={styles.studentList}>
            {students.map((s) => (
              <li className={styles.studentItem} key={s._id}>
                <Avatar src={s.avatar} size={34}>
                  {studentName(s).charAt(0)}
                </Avatar>
                <span className={styles.studentName}>{studentName(s)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );

  const body = loadingEdit ? (
    <div className={styles.center}>
      <Spin />
    </div>
  ) : current === 0 ? (
    renderStep1()
  ) : current === 1 ? (
    renderStep2()
  ) : current === 2 ? (
    renderStep3()
  ) : (
    renderStep4()
  );

  // ============== Footer ==============
  const renderPrimary = () => {
    if (current === 0) {
      return (
        <Tooltip title={canNextFrom1 ? "" : t("wizard.completeStep1")}>
          <Button type="primary" disabled={!canNextFrom1} onClick={goNext}>
            {t("wizard.next")} <ArrowRightOutlined />
          </Button>
        </Tooltip>
      );
    }
    if (current === 3) {
      // ultimo paso: activar (create/update + activate)
      return (
        <Tooltip title={canSave ? "" : t("wizard.needSteps")}>
          <Button
            type="primary"
            icon={<CheckCircleFilled />}
            loading={activating}
            disabled={!canSave}
            onClick={activateTask}
          >
            {t("wizard.activate")}
          </Button>
        </Tooltip>
      );
    }
    return (
      <Button type="primary" onClick={goNext}>
        {t("wizard.next")} <ArrowRightOutlined />
      </Button>
    );
  };

  return (
    <div className={styles.wizard}>
      <Steps
        current={current}
        items={wizardSteps.map((s) => ({ title: s.title, content: s.sub }))}
        className={styles.steps}
        size="small"
      />

      <div className={styles.content}>{body}</div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {current > 0 ? (
            <Button onClick={goBack}>{t("wizard.back")}</Button>
          ) : (
            <Button onClick={onClose}>{t("list.cancelButton")}</Button>
          )}
        </div>

        <div className={styles.footerRight}>
          {current >= 1 ? (
            <Tooltip title={canSave ? "" : t("wizard.needSteps")}>
              <Button
                icon={<SaveOutlined />}
                loading={creating}
                disabled={!canSave}
                onClick={saveDraft}
              >
                {isEdit ? t("wizard.saveChanges") : t("wizard.saveDraft")}
              </Button>
            </Tooltip>
          ) : null}
          {renderPrimary()}
        </div>
      </div>
    </div>
  );
}

export default TaskCreateWizard;
